import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import DeeperChat from "@/components/DeeperChat";
import { Loader2, Sparkles, BookMarked, Sprout, Search, LibraryBig, Clock, X, Layers } from "lucide-react";
import { toast } from "sonner";

const DEPTHS = [
  { id: "beginner", label: "Beginner" },
  { id: "intermediate", label: "Intermediate" },
  { id: "advanced", label: "Advanced" },
];

// Mirror of the backend _passage_key so we can spot a passage already studied.
function passageKey(ref) {
  if (!ref) return "";
  let s = ref.trim().replace(/:\s*[\d,\-\u2013\s]+$/, "");
  const m = s.match(/^\s*((?:[1-3]\s+)?[A-Za-z][A-Za-z.\s]*?)\s+(\d+)\b/);
  if (m) return `${m[1].replace(/\s+/g, " ").trim()} ${m[2]}`.toLowerCase();
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}

export default function StudySetup() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("passage");
  const [input, setInput] = useState("");
  const [depth, setDepth] = useState("intermediate");
  const [translation, setTranslation] = useState("niv");
  const [model, setModel] = useState("gpt-5.6-luna");
  const [starting, setStarting] = useState(false);
  const [companionChat, setCompanionChat] = useState(null);
  const [streak, setStreak] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [passages, setPassages] = useState([]);

  useEffect(() => {
    api.get("/study/passages").then(({ data }) => setPassages(data || [])).catch(() => {});
  }, []);

  const matchedPassage =
    mode === "passage" && input.trim()
      ? passages.find((p) => p.key === passageKey(input))
      : null;

  // Shown exactly once, ever — the moment it renders we mark it seen, so it never
  // reappears again even if the user doesn't click the dismiss button.
  useEffect(() => {
    if (!localStorage.getItem("owp_welcomed_v1")) {
      setShowWelcome(true);
      localStorage.setItem("owp_welcomed_v1", "1");
    }
  }, []);
  const dismissWelcome = () => setShowWelcome(false);

  // A visitor who arrived via a Daily Nugget funnel signs up, then lands here —
  // pre-fill the study with the verse they came from (the "continuity payoff").
  useEffect(() => {
    try {
      const raw = localStorage.getItem("owp_intended_study");
      if (raw) {
        const intent = JSON.parse(raw);
        if (intent?.input) {
          setMode(intent.mode || "passage");
          setInput(intent.input);
          toast.success("We carried your verse over — press Begin study when you're ready.");
        }
        localStorage.removeItem("owp_intended_study");
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get("/me/subscription")
      .then(({ data }) => setStreak(data.streak || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: settings }] = await Promise.all([
          api.get("/config"),
          api.get("/settings"),
        ]);
        setModel(settings.model || cfg.default_model);
        setTranslation(settings.translation || "niv");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, []);

  // Load the free-form companion history independently so the always-visible
  // "Can we go deeper?" FAB is never blocked by the config/settings requests.
  useEffect(() => {
    api.get("/companion/chat")
      .then(({ data }) => setCompanionChat(data.chat || []))
      .catch(() => setCompanionChat([]));
  }, []);

  const start = async () => {
    if (!input.trim()) {
      toast.error(mode === "passage" ? "Enter a passage reference" : "Enter a topic");
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post("/study/start", {
        input: input.trim(),
        mode,
        depth,
        translation,
        model,
        reflect_on_previous: false,
      });
      navigate(`/study/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the study");
    }
    setStarting(false);
  };

  return (
    <div className="px-6 pt-10 pb-40 space-y-10 md:max-w-2xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {showWelcome && (
        <div
          data-testid="first-run-welcome"
          className="relative rounded-2xl border border-[#00E5FF]/15 bg-[#10222A]/70 p-5 pr-11 animate-in fade-in slide-in-from-top-2 duration-500"
        >
          <button
            data-testid="dismiss-welcome"
            onClick={dismissWelcome}
            aria-label="Dismiss welcome"
            className="absolute top-3 right-3 text-[#00E5FF]/60 hover:text-[#00E5FF] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <p className="text-lg text-slate-200">
            {user?.name ? `Welcome, ${user.name.split(" ")[0]}.` : "Welcome, friend."}
          </p>
          <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">
            There&rsquo;s nothing to set up and nothing to prove here — just you and the Word. Bring a
            passage or a question below, and we&rsquo;ll sit with it together.
          </p>
        </div>
      )}
      <header>
        <div className="flex items-center gap-2 mb-2">
          <img src="/openword-logo.png" alt="" className="w-6 h-6" />
          <span className="text-xs font-medium uppercase tracking-[0.25em] text-[#00E5FF]">
            OpenWord AI
          </span>
        </div>
        <h1 className="text-3xl text-white leading-tight">
          {user?.name ? `Grace and peace, ${user.name}.` : "Welcome."}
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">Bring a passage or a question below.</p>
        {streak && streak.count >= 1 && (
          <div
            data-testid="streak-banner"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#10222A] border border-[#00E5FF]/15 px-4 py-2 text-sm text-[#00E5FF]"
          >
            <Sprout className="w-4 h-4" strokeWidth={1.5} />
            {streak.active_today
              ? `A ${streak.count}-day rhythm — grace to you for showing up today.`
              : `You're on a ${streak.count}-day rhythm. A few quiet minutes keeps it growing.`}
          </div>
        )}
      </header>

      {/* Explore shortcuts (mobile — desktop has these in the sidebar) */}
      <div className="grid grid-cols-3 gap-2 md:hidden">
        <button
          data-testid="explore-history"
          onClick={() => navigate("/history")}
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#00E5FF]/30 bg-[#10222A]/60 py-2.5 text-xs font-medium text-[#00E5FF] hover:border-[#00E5FF]/50 hover:bg-[#10222A] transition-colors"
        >
          <Clock className="w-4 h-4" strokeWidth={1.5} /> My Studies
        </button>
        <button
          data-testid="explore-search"
          onClick={() => navigate("/search")}
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#29344D] bg-[#0E131F] py-2.5 text-xs font-medium text-slate-400 hover:border-[#00E5FF]/40 hover:text-[#00E5FF] transition-colors"
        >
          <Search className="w-4 h-4" strokeWidth={1.5} /> Search
        </button>
        <button
          data-testid="explore-resources"
          onClick={() => navigate("/resources")}
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#29344D] bg-[#0E131F] py-2.5 text-xs font-medium text-slate-400 hover:border-[#00E5FF]/40 hover:text-[#00E5FF] transition-colors"
        >
          <LibraryBig className="w-4 h-4" strokeWidth={1.5} /> Resources
        </button>
      </div>

      {/* Mode toggle */}
      <div className="space-y-3">
        <label className="text-xs font-medium uppercase tracking-widest text-slate-400">Study by</label>
        <div className="grid grid-cols-2 gap-2 p-1 bg-[#141B29] rounded-xl">
          {[
            { id: "passage", label: "Passage", icon: BookMarked },
            { id: "topic", label: "Topic", icon: Sparkles },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`mode-${id}`}
              onClick={() => setMode(id)}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                mode === id ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </button>
          ))}
        </div>
        <Textarea
          data-testid="study-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={mode === "passage" ? "e.g. John 3, Psalm 23" : "e.g. anxiety, forgiveness"}
          rows={2}
          className="rounded-xl border-[#29344D] bg-[#0E131F] resize-none text-base placeholder:text-slate-500 focus-visible:ring-[#00E5FF]/30"
        />
        {matchedPassage && (
          <button
            data-testid="studied-before-prompt"
            onClick={() => navigate(`/passages/${encodeURIComponent(matchedPassage.key)}`)}
            className="w-full flex items-center gap-2.5 rounded-xl border border-[#00E5FF]/25 bg-[#10222A]/60 px-4 py-2.5 text-left text-sm text-[#00E5FF] hover:bg-[#10222A] transition-colors animate-in fade-in slide-in-from-top-1 duration-300"
          >
            <Layers className="w-4 h-4 shrink-0" strokeWidth={1.75} />
            <span>
              You've studied <strong>{matchedPassage.label}</strong> {matchedPassage.count} {matchedPassage.count === 1 ? "time" : "times"} before — see your history &amp; combine it
            </span>
          </button>
        )}
      </div>

      {/* Depth */}
      <div className="space-y-3">
        <label className="text-xs font-medium uppercase tracking-widest text-slate-400">Depth</label>
        <div className="grid grid-cols-3 gap-2">
          {DEPTHS.map((d) => (
            <button
              key={d.id}
              data-testid={`depth-${d.id}`}
              onClick={() => setDepth(d.id)}
              className={`py-3 rounded-xl text-sm font-medium border transition-colors ${
                depth === d.id
                  ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]"
                  : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:border-[#00E5FF]/40"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <Button
        data-testid="start-study"
        onClick={start}
        disabled={starting}
        className="w-full h-14 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-base font-medium"
      >
        {starting ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Preparing your study…
          </span>
        ) : (
          "Begin study"
        )}
      </Button>

      <p className="text-xs text-slate-500 text-center leading-relaxed px-4">
        This virtual study companion is a study tool, not a replacement for a pastor, a church, or the Bible itself.
      </p>

      {/* Always-visible "Can we go deeper?" — opens a free-form companion conversation */}
      {companionChat !== null && (
        <DeeperChat variant="floating" general initialChat={companionChat} />
      )}
    </div>
  );
}
